import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, Trash2, Layers, ChevronLeft } from '../DeskeyIcons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { friendlyError, notifyError } from '../../lib/errors';
import { confirmDialog } from '../ConfirmDialog';
import { institutionApi } from '../../lib/institutionApi';
import Loader from '../Loader';

const api = institutionApi;
const SAVE_FAILED = 'Could not save the subject.';

export default function SubjectsSection({ accent = '#34A853', showToast }) {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [selected, setSelected] = useState(null);
  const [opening, setOpening] = useState(false);
  const A = accent;

  const load = useCallback(async () => {
    setLoading(true);
    try { setSubjects(await api('/subjects') || []); } catch (e) { notifyError(e, 'Could not load subjects.'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createSubject = async () => {
    const names = [...new Set(newName.split(',').map(n => n.trim()).filter(Boolean))];
    if (names.length === 0) return;
    let created = 0;
    const errors = [];
    for (const name of names) {
      try {
        await api('/subjects', { method: 'POST', body: JSON.stringify({ name }) });
        created++;
      } catch (e) { errors.push(`${name}: ${friendlyError(e, 'could not be created')}`); }
    }
    if (created > 0) {
      setNewName(''); setShowCreate(false);
      showToast?.(created === 1 ? 'Subject created' : `${created} subjects created`);
    }
    if (errors.length > 0) showToast?.(errors.join('; '));
    load();
  };

  const deleteSubject = async (subjectId, name) => {
    if (!(await confirmDialog({ title: 'Delete subject', message: `Delete "${name}"?`, danger: true, confirmLabel: 'Delete' }))) return;
    try { await api(`/subjects/${subjectId}`, { method: 'DELETE' }); }
    catch (e) { showToast?.(friendlyError(e, SAVE_FAILED)); return; }
    showToast?.('Subject deleted');
    load();
  };

  const openDetail = async (subjectId) => {
    setOpening(true);
    try { setSelected(await api(`/subjects/${subjectId}`)); }
    catch (e) { showToast?.(friendlyError(e, 'Could not open this subject.')); }
    setOpening(false);
  };

  const removeLink = async (subjectId, linkId) => {
    if (!(await confirmDialog({ title: 'Remove class link', message: 'Remove this class link?', danger: true, confirmLabel: 'Remove' }))) return;
    try { await api(`/subjects/${subjectId}/links/${linkId}`, { method: 'DELETE' }); }
    catch (e) { showToast?.(friendlyError(e, SAVE_FAILED)); return; }
    openDetail(subjectId);
    load();
  };

  if (loading) return <Loader />;
  if (opening && !selected) return <Loader label="Loading subject…" />;

  if (selected) {
    return (
      <div data-testid="subject-detail">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"><ChevronLeft className="w-4 h-4" /> Back to Subjects</button>
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${A}14` }}><BookOpen className="w-4 h-4" style={{ color: A }} /></div>
          <div><p className="text-sm font-bold text-gray-900">{selected.name}</p><p className="text-[11px] text-gray-400">{(selected.links || []).length} linked class{(selected.links || []).length !== 1 ? 'es' : ''}</p></div>
        </div>
        {(selected.links || []).length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-10 text-center">
            <Layers className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-gray-500">Not linked to any class yet</p>
            <p className="text-xs text-gray-400 mt-1">Link it from a class's Subjects &amp; Teachers tab</p>
          </div>
        ) : (
          <div className="space-y-2">
            {selected.links.map(link => (
              <div key={link.id} className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3" data-testid={`subject-link-${link.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{link.teacher_name || 'No teacher assigned'}</p>
                  <p className="text-[11px] text-gray-400">{link.class_name || link.class_id} &middot; {link.department_name || link.department_id || '—'}</p>
                </div>
                <button onClick={() => removeLink(selected.id, link.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500" data-testid={`remove-link-${link.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="subjects-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Subjects</h3>
        <Button onClick={() => setShowCreate(true)} className="h-9 px-4 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid="new-subject-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Subject
        </Button>
      </div>

      {showCreate && (
        <div className="flex items-center gap-2 p-3 bg-white rounded-xl border-2 border-dashed mb-4" style={{ borderColor: `${A}40` }} data-testid="subject-create-form">
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Subject name(s), e.g. Physics, Chemistry, Biology" className="h-9 flex-1 rounded-lg text-sm" autoFocus data-testid="subject-name-input" onKeyDown={e => e.key === 'Enter' && createSubject()} />
          <Button onClick={createSubject} disabled={!newName.trim()} className="h-9 px-4 rounded-lg text-white text-xs" style={{ background: A }} data-testid="save-subject-btn">Create</Button>
          <button onClick={() => { setShowCreate(false); setNewName(''); }} className="text-xs text-gray-400 px-2">Cancel</button>
        </div>
      )}

      {subjects.length === 0 ? (
        <div className="text-center py-14"><Layers className="w-10 h-10 mx-auto mb-3" style={{ color: `${A}30` }} /><p className="text-sm text-gray-500">No subjects yet</p></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {subjects.map(s => (
            <div key={s.id} onClick={() => openDetail(s.id)} className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer group hover:shadow-md hover:-translate-y-[1px] transition-all relative" data-testid={`subject-card-${s.id}`}>
              <button onClick={(e) => { e.stopPropagation(); deleteSubject(s.id, s.name); }} className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all" data-testid={`delete-subject-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2.5" style={{ background: `${A}12` }}><BookOpen className="w-4 h-4" style={{ color: A }} /></div>
              <h4 className="text-[13px] font-bold text-gray-900 truncate">{s.name}</h4>
              <span className="text-[10px] font-semibold mt-1.5 block" style={{ color: A }}>{s.link_count || 0} class{s.link_count !== 1 ? 'es' : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
